// src/contexts/CartProvider.tsx
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { guestCart, GuestCartItem } from "../services/guestCart";
import { useAuth } from "./AuthProvider";
import {
  cartAdd,
  cartClear,
  cartDelete,
  cartGetList,
  cartUpdate,
  CartLine,
} from "../api/cartApi";

/* ======================
   TYPES (UI dùng)
====================== */
export interface CartItem {
  /** UI dùng variantId như "cart line id" để update/remove */
  variantId: number;
  quantity: number;
  unitPrice: number;

  productName?: string;
  imageUrl?: string;

  color?: string | null;
  size?: string | null;
}

interface CartContextType {
  items: CartItem[];
  count: number;

  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  /** ✅ Add bắt buộc color + size theo backend */
  addItem: (payload: {
    productId: number;
    color: string;
    size: string;
    quantity: number;
  }) => Promise<void>;

  /** update/remove dùng cartLineId (UI gọi là variantId) */
  updateItem: (cartLineId: number, quantity: number) => Promise<void>;
  removeItem: (cartLineId: number) => Promise<void>;
  clear: () => Promise<void>;

  reloadCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function safeNumber(v: any, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Mapping:
 * - CartLine.cartId: id dòng trong cart (để update/delete)
 * - UI dùng variantId = cartId
 */
function mapLineToItem(line: CartLine): CartItem {
  return {
    variantId: safeNumber(line.cartId),
    quantity: safeNumber(line.quantity),
    unitPrice: safeNumber(line.unitPrice),
    productName: line.productName ?? undefined,
    imageUrl: line.imageUrl ?? undefined,
    color: line.color ?? null,
    size: line.size ?? null,
  };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((v) => !v);

  // ======================
  // LOAD CART
  // ======================
  const loadCart = async () => {
    try {
      const lines = await cartGetList();
      setItems((lines ?? []).map(mapLineToItem));
    } catch (err) {
      console.error("Load cart failed", err);

      // fallback guest local (chỉ để hiển thị khi API chết)
      const guest = guestCart.get();
      const mapped: CartItem[] = (guest.items ?? []).map((i: GuestCartItem) => ({
        variantId: safeNumber(i.variantId),
        quantity: safeNumber(i.quantity),
        unitPrice: safeNumber((i as any).unitPrice, 0),
        productName: (i as any).productName,
        imageUrl: (i as any).imageUrl,
        color: (i as any).color ?? null,
        size: (i as any).size ?? null,
      }));
      setItems(mapped);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await loadCart();
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ======================
  // ADD (bắt buộc color + size)
  // ======================
  const addItem: CartContextType["addItem"] = async (payload) => {
    const quantity = safeNumber(payload.quantity);
    const productId = safeNumber(payload.productId);

    if (!productId || quantity <= 0) return;

    const color = (payload.color ?? "").trim();
    const size = (payload.size ?? "").trim();

    if (!color || !size) {
      throw new Error("Vui lòng chọn màu và size trước khi thêm vào giỏ.");
    }

    // ✅ backend lấy userId từ token, guest dùng guestId (cartApi tự gắn)
    await cartAdd({
      productId,
      color,
      size,
      quantity,
    });

    // fallback local chỉ để hiển thị nếu user chưa login + API chết.
    // Lưu ý: guestCart local của bạn đang lưu variantId kiểu productId (không phải cartId),
    // nên KHÔNG dùng để update/remove chuẩn được => chỉ giữ tối thiểu.
    if (!user) {
      guestCart.add({
        variantId: productId,
        quantity,
        productName: (payload as any).productName,
        imageUrl: (payload as any).imageUrl,
      } as any);
    }

    await loadCart();
    open();
  };

  // ======================
  // UPDATE (cartId)
  // ======================
  const updateItem: CartContextType["updateItem"] = async (cartLineId, quantity) => {
    const id = safeNumber(cartLineId);
    const qty = safeNumber(quantity);

    if (!id || qty < 0) return;

    if (qty === 0) {
      await removeItem(id);
      return;
    }

    await cartUpdate({ cartId: id, quantity: qty });
    await loadCart();

    // guestCart local không map theo cartId => không update local ở đây nữa
  };

  // ======================
  // REMOVE (cartId)
  // ======================
  const removeItem: CartContextType["removeItem"] = async (cartLineId) => {
    const id = safeNumber(cartLineId);
    if (!id) return;

    await cartDelete(id);
    await loadCart();

    // guestCart local không map theo cartId => không remove local ở đây nữa
  };

  // ======================
  // CLEAR
  // ======================
  const clear = async () => {
    await cartClear();
    setItems([]);

    // local fallback
    if (!user) guestCart.clear();
  };

  const count = useMemo(
    () => items.reduce((sum, i) => sum + safeNumber(i.quantity, 0), 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        count,

        isOpen,
        open,
        close,
        toggle,

        addItem,
        updateItem,
        removeItem,
        clear,

        reloadCart: loadCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
